import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — SettingsPanel
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 6, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 4, scale: 0.97 },
};
const PANEL_TRANSITION = { duration: 0.15, ease: [0.16, 1, 0.3, 1] };

const ObliqueSwitch = ({ checked, onChange }) => (
    <div 
        onClick={(e) => { e.stopPropagation(); onChange(!checked); }} 
        className={`w-8 h-[18px] rounded-full relative cursor-pointer shrink-0 transition-colors duration-200 ${checked ? 'bg-[#2383e2]' : 'bg-[#e2e2e2]'}`}
    >
        <div 
            className="w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-[left] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
            style={{ left: checked ? '16px' : '2px' }} 
        />
    </div>
);

export function SettingsPanel({ 
    onClose, onExport, onReport, onCleanup, 
    pageActions = [], showTiles, onShowTilesChange, 
    viewMode = 'Table', columns = [], hiddenColumns = [], 
    onToggleColumn, onLayoutChange, metricsConfig, 
    activeObject, objectOptions = [], onObjectChange, propertyCount,
    viewSettings = {}, onViewSettingsChange,
    onOpenFilter, onOpenSort
}) {
    const [viewName, setViewName] = useState('Table');
    const [isCopied, setIsCopied] = useState(false);
    const [isLocked, setIsLocked] = useState(viewSettings?.isLocked || false);
    const [rowHeight, setRowHeight] = useState(viewSettings?.rowHeight || 'default');
    const [wrapContent, setWrapContent] = useState(viewSettings?.wrapContent || false);
    const [isDuplicated, setIsDuplicated] = useState(false);
    const [isReset, setIsReset] = useState(false);
    const [activeMenu, setActiveMenu] = useState('main');

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleRowHeightCycle = () => {
        const heights = ['compact', 'default', 'comfortable'];
        const nextIdx = (heights.indexOf(rowHeight) + 1) % heights.length;
        const nextVal = heights[nextIdx];
        setRowHeight(nextVal);
        onViewSettingsChange?.({ ...viewSettings, rowHeight: nextVal });
    };

    const handleToggleWrap = (val) => {
        setWrapContent(val);
        onViewSettingsChange?.({ ...viewSettings, wrapContent: val });
    };

    const handleToggleLock = () => {
        const nextVal = !isLocked;
        setIsLocked(nextVal);
        onViewSettingsChange?.({ ...viewSettings, isLocked: nextVal });
    };

    const handleDuplicateView = () => {
        setIsDuplicated(true);
        setTimeout(() => setIsDuplicated(false), 2000);
    };

    const handleResetView = () => {
        setRowHeight('default');
        setWrapContent(false);
        setIsLocked(false);
        onViewSettingsChange?.({ rowHeight: 'default', wrapContent: false, isLocked: false });
        setIsReset(true);
        setTimeout(() => setIsReset(false), 2000);
    };

    const SettingsRow = ({ icon, label, right, onClick, danger }) => (
        <button
            onClick={onClick}
            className={`w-full flex items-center justify-between py-1.5 px-2 rounded-md border-none bg-transparent ${onClick ? 'cursor-pointer hover:bg-[var(--bg-subtle)]' : 'cursor-default'} ${danger ? 'text-[var(--danger)] hover:bg-[var(--danger)]/10' : 'text-[var(--text-primary)]'} text-[13px] text-left outline-none transition-colors duration-150 leading-none`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <div className="flex items-center justify-center shrink-0 w-4 h-4">
                    <ObliqueIcon name={icon} size={15} color={danger ? 'var(--danger)' : 'var(--text-secondary)'} />
                </div>
                <span className="font-normal truncate">{label}</span>
            </div>
            {right && (
                <div className="flex items-center gap-1 text-[12px] text-[var(--text-tertiary)] shrink-0 ml-2">
                    {right}
                </div>
            )}
        </button>
    );

    const renderMain = () => (
        <>
            {/* Header: Title and Close button */}
            <div className="flex items-center justify-between px-2.5 py-1.5 mb-1 text-[13px] font-medium text-[var(--text-secondary)] border-b border-[var(--border-subtle)]">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">View settings</span>
                <button 
                    onClick={onClose} 
                    className="border-none bg-transparent cursor-pointer p-0.5 rounded flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors"
                >
                    <ObliqueIcon name="close" size={14} />
                </button>
            </div>

            {/* Category 1: View Configuration */}
            <div className="flex flex-col gap-[1px]">
                <SettingsRow 
                    icon={
                        String(viewMode).toLowerCase() === 'board' ? 'view_week' :
                        String(viewMode).toLowerCase() === 'list' ? 'format_list_bulleted' :
                        String(viewMode).toLowerCase() === 'gallery' ? 'grid_view' :
                        String(viewMode).toLowerCase() === 'timeline' ? 'view_timeline' :
                        String(viewMode).toLowerCase() === 'calendar' ? 'calendar_today' :
                        'table_chart'
                    } 
                    label="Layout" 
                    right={<><span className="capitalize">{viewMode || 'Table'}</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                    onClick={() => setActiveMenu('layout')}
                />
                <SettingsRow 
                    icon="visibility" 
                    label="Property visibility" 
                    right={<><span>{columns.length ? columns.length - hiddenColumns.length : (propertyCount || 14)}</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                    onClick={() => setActiveMenu('properties')}
                />
                <SettingsRow 
                    icon="format_line_spacing" 
                    label="Row height" 
                    right={<span className="capitalize px-1.5 py-0.5 rounded bg-[var(--bg-muted)] text-[11px] font-medium text-[var(--text-secondary)]">{rowHeight}</span>} 
                    onClick={handleRowHeightCycle}
                />
                <div className="w-full flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-[var(--bg-subtle)] text-[13px] transition-colors duration-150">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="flex items-center justify-center shrink-0 w-4 h-4">
                            <ObliqueIcon name="wrap_text" size={15} color="var(--text-secondary)" />
                        </div>
                        <span className="font-normal truncate text-[var(--text-primary)]">Wrap content</span>
                    </div>
                    <ObliqueSwitch checked={wrapContent} onChange={handleToggleWrap} />
                </div>
                <SettingsRow 
                    icon="analytics" 
                    label="Metrics" 
                    right={<><span className="text-[12px] text-[var(--text-tertiary)]">{metricsConfig?.visibleIds ? `${metricsConfig.visibleIds.length} active` : (showTiles !== false ? 'Shown' : 'Hidden')}</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                    onClick={() => setActiveMenu('metrics')}
                />
                <SettingsRow 
                    icon="filter_alt" 
                    label="Filter" 
                    right={<ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" />} 
                    onClick={() => { onClose(); onOpenFilter && onOpenFilter(); }}
                />
                <SettingsRow 
                    icon="swap_vert" 
                    label="Sort" 
                    right={<ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" />} 
                    onClick={() => { onClose(); onOpenSort && onOpenSort(); }}
                />
                <SettingsRow 
                    icon="apps" 
                    label="Group" 
                    right={<><span>Status</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                />
                <SettingsRow 
                    icon="color_lens" 
                    label="Conditional color" 
                    right={<ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" />} 
                />
            </div>

            <div className="h-px bg-[var(--border-subtle)] my-1" />

            {/* Category 2: View Actions & Maintenance */}
            <div className="flex flex-col gap-[1px]">
                {objectOptions.length > 0 && (
                    <div className="px-1 py-1">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] px-1 mb-1 flex items-center gap-1.5">
                            <ObliqueIcon name="layers" size={12} color="var(--text-tertiary)" />
                            Object Type
                        </div>
                        <div className="flex bg-[var(--bg-subtle)] rounded-md p-0.5 border border-[var(--border-subtle)] gap-0.5">
                            {objectOptions.map(obj => {
                                const isActive = activeObject === obj.value;
                                return (
                                    <button
                                        key={obj.value}
                                        onClick={() => onObjectChange?.(obj.value)}
                                        className={`flex-1 relative flex items-center justify-center py-1 px-1.5 rounded border-none text-[12px] cursor-pointer gap-1 transition-all duration-150 ${
                                            isActive 
                                                ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] font-medium shadow-xs border border-[var(--border-subtle)]' 
                                                : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-normal'
                                        }`}
                                    >
                                        <ObliqueIcon name={obj.icon || 'layers'} size={13} color={isActive ? "var(--accent)" : "var(--text-tertiary)"} />
                                        <span className="truncate">{obj.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <SettingsRow 
                    icon="link" 
                    label={isCopied ? "Link copied to clipboard!" : "Copy link to view"} 
                    onClick={handleCopyLink}
                />
                <SettingsRow 
                    icon="control_point_duplicate" 
                    label={isDuplicated ? "View duplicated!" : "Duplicate view"} 
                    onClick={handleDuplicateView}
                />
                <div className="w-full flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-[var(--bg-subtle)] text-[13px] transition-colors duration-150">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="flex items-center justify-center shrink-0 w-4 h-4">
                            <ObliqueIcon name={isLocked ? "lock" : "lock_open"} size={15} color="var(--text-secondary)" />
                        </div>
                        <span className="font-normal truncate text-[var(--text-primary)]">Lock view</span>
                    </div>
                    <ObliqueSwitch checked={isLocked} onChange={handleToggleLock} />
                </div>
                <SettingsRow 
                    icon="restart_alt" 
                    label={isReset ? "Reset completed" : "Reset to default view"} 
                    onClick={handleResetView}
                    danger={false}
                />
            </div>
        </>
    );

    const renderLayout = () => {
        const layouts = [
            { id: 'Table', icon: 'table_chart', label: 'Table' },
            { id: 'Board', icon: 'view_week', label: 'Board' },
            { id: 'Timeline', icon: 'view_timeline', label: 'Timeline' },
            { id: 'Calendar', icon: 'calendar_today', label: 'Calendar' },
            { id: 'List', icon: 'format_list_bulleted', label: 'List' },
            { id: 'Gallery', icon: 'grid_view', label: 'Gallery' },
            { id: 'Chart', icon: 'pie_chart', label: 'Chart' },
            { id: 'Report', icon: 'insert_chart', label: 'Report' },
            { id: 'Feed', icon: 'feed', label: 'Feed' },
            { id: 'Map', icon: 'map', label: 'Map' },
            { id: 'Dashboard', icon: 'dashboard', label: 'Dashboard' }
        ];

        return (
            <div className="flex flex-col p-1">
                <div className="flex items-center pt-1 px-2 pb-2">
                    <button 
                        onClick={() => setActiveMenu('main')}
                        className="border-none bg-transparent cursor-pointer flex items-center p-1 rounded mr-2 hover:bg-[var(--bg-muted)]"
                    >
                        <ObliqueIcon name="arrow_back" size={15} color="var(--text-secondary)" />
                    </button>
                    <span className="text-[13px] font-medium text-[var(--text-secondary)]">Layout</span>
                </div>
                
                {/* 3 Column Grid for Layouts */}
                <div className="py-2 px-3">
                    <div className="grid grid-cols-3 gap-2">
                        {layouts.map(l => {
                            const isActive = String(viewMode || 'table').toLowerCase() === l.id.toLowerCase();
                            return (
                                <button
                                    key={l.id}
                                    onClick={() => onLayoutChange && onLayoutChange(l.id)}
                                    className={`flex flex-col items-center justify-center py-3 px-1 rounded-md cursor-pointer gap-1.5 transition-all duration-150 ${isActive ? 'bg-[rgba(35,131,226,0.08)] border border-[#2383e2] shadow-[0_0_0_1px_rgba(35,131,226,0.3)]' : 'bg-transparent border border-[var(--border-subtle)] hover:bg-[var(--bg-subtle)]'}`}
                                >
                                    <ObliqueIcon name={l.icon} size={18} color={isActive ? '#2383e2' : 'var(--text-tertiary)'} />
                                    <span className={`text-[11px] font-medium ${isActive ? 'text-[#2383e2] font-semibold' : 'text-[var(--text-secondary)]'}`}>{l.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                
                <div className="h-px bg-[var(--border-subtle)] mx-3 my-1" />
                
                <div className="py-2 px-1">
                    <SettingsRow 
                        icon="vertical_align_center" 
                        label="Show vertical lines" 
                        right={<ObliqueSwitch checked={viewSettings?.showVerticalLines !== false} onChange={(val) => onViewSettingsChange && onViewSettingsChange({ ...viewSettings, showVerticalLines: val })} />} 
                    />
                    <SettingsRow 
                        icon="description" 
                        label="Show page icon" 
                        right={<ObliqueSwitch checked={viewSettings?.showPageIcon !== false} onChange={(val) => onViewSettingsChange && onViewSettingsChange({ ...viewSettings, showPageIcon: val })} />} 
                    />
                    <SettingsRow 
                        icon="wrap_text" 
                        label="Wrap all content" 
                        right={<ObliqueSwitch checked={!!viewSettings?.wrapContent} onChange={(val) => onViewSettingsChange && onViewSettingsChange({ ...viewSettings, wrapContent: val })} />} 
                    />
                    <SettingsRow 
                        icon="view_sidebar" 
                        label="Open pages in" 
                        onClick={() => {
                            const modes = ['Side peek', 'Center peek', 'Full page'];
                            const current = viewSettings?.openPageIn || 'Side peek';
                            const next = modes[(modes.indexOf(current) + 1) % modes.length];
                            onViewSettingsChange && onViewSettingsChange({ ...viewSettings, openPageIn: next });
                        }}
                        right={<><span className="text-[12px] text-[var(--text-tertiary)]">{viewSettings?.openPageIn || 'Side peek'}</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                    />
                </div>
            </div>
        );
    };

    const renderMetrics = () => {
        const { allMetrics = [], visibleIds = [], onToggle } = metricsConfig || {};
        const actualVisibleCount = allMetrics.filter(m => visibleIds?.includes(m.id)).length;
        
        return (
            <div className="flex flex-col p-1">
                <div className="flex items-center pt-1 px-2 pb-2">
                    <button 
                        onClick={() => setActiveMenu('main')}
                        className="border-none bg-transparent cursor-pointer flex items-center p-1 rounded mr-2 hover:bg-[var(--bg-muted)]"
                    >
                        <ObliqueIcon name="arrow_back" size={15} color="var(--text-secondary)" />
                    </button>
                    <span className="text-[13px] font-medium text-[var(--text-secondary)]">Metrics</span>
                    {allMetrics.length > 0 && (
                        <span className="ml-auto text-[12px] text-[var(--text-tertiary)]">({actualVisibleCount}/{allMetrics.length})</span>
                    )}
                </div>
                
                {allMetrics.length > 0 && (
                    <div className="py-2 px-3">
                        <div className="grid grid-cols-3 gap-2">
                            {allMetrics.map(m => {
                                const isSelected = visibleIds?.includes(m.id);
                                return (
                                    <button
                                        key={m.id}
                                        onClick={() => onToggle && onToggle(m.id)}
                                        className={`flex flex-col items-center justify-center py-2.5 px-1 rounded-md gap-1.5 transition-all duration-150 cursor-pointer hover:bg-[var(--bg-subtle)] ${isSelected ? 'bg-[rgba(35,131,226,0.08)] border border-[#2383e2] shadow-[0_0_0_1px_rgba(35,131,226,0.3)]' : 'bg-transparent border border-[var(--border-subtle)]'}`}
                                    >
                                        <ObliqueIcon name={m.icon || 'analytics'} size={18} color={isSelected ? '#2383e2' : 'var(--text-tertiary)'} />
                                        <span className={`text-[11px] font-medium text-center ${isSelected ? 'text-[#2383e2] font-semibold' : 'text-[var(--text-secondary)]'}`}>{m.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                
                <div className="h-px bg-[var(--border-subtle)] mx-3 my-1" />
                
                <div className="py-2 px-1">
                    <SettingsRow 
                        icon="grid_view" 
                        label="Show Metrics Bar" 
                        right={<ObliqueSwitch checked={showTiles !== false} onChange={() => onShowTilesChange && onShowTilesChange(showTiles === false)} />} 
                    />
                </div>
            </div>
        );
    };

    const renderProperties = () => (
        <div className="flex flex-col p-1">
            <div className="flex items-center pt-1 px-2 pb-2">
                <button 
                    onClick={() => setActiveMenu('main')}
                    className="border-none bg-transparent cursor-pointer flex items-center p-1 rounded mr-2 hover:bg-[var(--bg-muted)]"
                >
                    <ObliqueIcon name="arrow_back" size={15} color="var(--text-secondary)" />
                </button>
                <span className="text-[13px] font-medium text-[var(--text-secondary)]">Properties</span>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                {columns.map(col => {
                    const isHidden = hiddenColumns.includes(col.id);
                    return (
                        <div key={col.id} className="flex items-center justify-between py-1.5 px-3 rounded-md hover:bg-[var(--bg-subtle)]">
                            <div className="flex items-center gap-2">
                                <ObliqueIcon name="drag_indicator" size={14} color="var(--text-tertiary)" className="cursor-grab" />
                                <span className={`text-[13px] ${isHidden ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-primary)]'}`}>{col.label}</span>
                            </div>
                            <button 
                                onClick={() => onToggleColumn && onToggleColumn(col.id)}
                                className="border-none bg-transparent cursor-pointer flex items-center p-0.5"
                            >
                                <ObliqueIcon name={isHidden ? "visibility_off" : "visibility"} size={16} color={isHidden ? "var(--text-tertiary)" : "var(--text-primary)"} />
                            </button>
                        </div>
                    );
                })}
                {columns.length === 0 && (
                    <div className="p-3 text-[12px] text-[var(--text-tertiary)] text-center">No properties available.</div>
                )}
            </div>
        </div>
    );

    const renderTableSettings = () => (
        <div className="flex flex-col p-1">
            <div className="flex items-center pt-1 px-2 pb-2">
                <button 
                    onClick={() => setActiveMenu('main')}
                    className="border-none bg-transparent cursor-pointer flex items-center p-1 rounded mr-2 hover:bg-[var(--bg-muted)]"
                >
                    <ObliqueIcon name="arrow_back" size={15} color="var(--text-secondary)" />
                </button>
                <span className="text-[13px] font-medium text-[var(--text-secondary)]">Table settings</span>
            </div>
            
            <div className="py-2 px-1">
                <div className="text-[10.5px] font-medium text-[var(--text-tertiary)] uppercase tracking-[0.05em] pt-1 px-3 pb-2">For this view</div>
                <SettingsRow 
                    icon="view_column" 
                    label="Edit columns" 
                    right={<ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" />} 
                    onClick={() => setActiveMenu('properties')}
                />
            </div>
            <div className="h-px bg-[var(--border-subtle)] mx-3 my-1" />
            
            <div className="py-2 px-1">
                <div className="text-[10.5px] font-medium text-[var(--text-tertiary)] uppercase tracking-[0.05em] pt-1 px-3 pb-2">Across all views</div>
                <SettingsRow 
                    icon="format_list_numbered" 
                    label="Pagination" 
                    right={<><span>Infinite</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                />
                <SettingsRow 
                    icon="height" 
                    label="Row height" 
                    right={<><span>Default</span><ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" /></>} 
                />
                <SettingsRow 
                    icon="format_line_spacing" 
                    label="Zebra striping" 
                    right={<ObliqueSwitch checked={false} onChange={() => {}} />} 
                />
            </div>
        </div>
    );

    return (
        <motion.div
            variants={PANEL_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={PANEL_TRANSITION}
            className="absolute top-[calc(100%+8px)] right-0 w-[260px] bg-[var(--bg-surface)] rounded-xl border border-[var(--border-subtle)] shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[2000] max-h-[75vh] overflow-y-auto overflow-x-hidden p-1"
        >
            {activeMenu === 'main' && renderMain()}
            {activeMenu === 'layout' && renderLayout()}
            {activeMenu === 'properties' && renderProperties()}
            {activeMenu === 'metrics' && renderMetrics()}
            {activeMenu === 'table' && renderTableSettings()}
        </motion.div>
    );
}
